// Base API Client Configuration
// Contains axios setup, interceptors, and base request utilities

import axios from 'axios';
import { supabase } from '../../lib/supabase';

const isCanceledRequest = (error) =>
  axios.isCancel?.(error) || error?.code === 'ERR_CANCELED' || error?.message === 'canceled';

// Resolve API base URL dynamically to support branch-specific deployments and different environments
const resolveApiBaseUrl = () => {
  try {
    // Prefer explicit environment variables (Vite style)
    const viteEnvUrl = import.meta.env.VITE_API_BASE_URL;
    if (viteEnvUrl && String(viteEnvUrl).trim()) return String(viteEnvUrl).trim();

    // Fallback to Create React App style for compatibility
    const envUrl = (typeof process !== 'undefined' && process && process.env && (process.env.REACT_APP_API_BASE_URL || process.env.API_BASE_URL));
    if (envUrl && String(envUrl).trim()) return String(envUrl).trim();

    // Allow runtime global override if injected on window
    const globalUrl = (typeof window !== 'undefined' && (window.__API_BASE_URL__ || window.REACT_APP_API_BASE_URL));
    if (globalUrl && String(globalUrl).trim()) return String(globalUrl).trim();

    // In browsers, default to relative '/api' so the frontend uses its current origin (works for branch previews)
    if (typeof window !== 'undefined') return '/api';
  } catch { }
  // Fallback for non-browser contexts (e.g., node scripts)
  return 'http://localhost:8000/api';
};

export const API_BASE_URL = resolveApiBaseUrl();

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Accept': 'application/json',
  },
  timeout: 600000
});

// Add request interceptor for logging and Authentication
apiClient.interceptors.request.use(
  async (config) => {
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) {
        config.headers['Authorization'] = `Bearer ${session.access_token}`;
      }
    } catch (e) {
      console.error("Failed to attach auth token", e);
    }

    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Transient-gateway-error retry: 502/503/504 mean the proxy never got a valid
// response from the backend at all (deploy-window gap, or a gunicorn worker
// killed mid-request) — the request almost certainly never reached application
// code, so retrying is safe for the read/analytics-query traffic this app is
// dominated by. GET and POST both retry (most tile data fetches are POST
// queries carrying a filter body, not mutations — see useGlobalAPI), but
// multipart/form-data requests (file uploads) are skipped since a retry there
// really could double-submit.
const RETRYABLE_STATUS = new Set([502, 503, 504]);
const MAX_GATEWAY_RETRIES = 2;
const isMultipartRequest = (config) => {
  const contentType = config?.headers?.['Content-Type'] || config?.headers?.['content-type'];
  return typeof contentType === 'string' && contentType.includes('multipart/form-data');
};
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Add response interceptor for logging and error handling
apiClient.interceptors.response.use(
  (response) => {
    console.log(`API Response: ${response.status} ${response.config.url}`);
    return response;
  },
  async (error) => {
    if (isCanceledRequest(error)) {
      return Promise.reject(error);
    }

    console.error('API Response Error:', error.response?.status, error.response?.data);

    const config = error.config;
    if (
      config &&
      RETRYABLE_STATUS.has(error.response?.status) &&
      !isMultipartRequest(config) &&
      (config.__gatewayRetryCount || 0) < MAX_GATEWAY_RETRIES
    ) {
      config.__gatewayRetryCount = (config.__gatewayRetryCount || 0) + 1;
      const delay = 400 * 2 ** (config.__gatewayRetryCount - 1);
      console.warn(
        `Gateway error ${error.response.status} on ${config.url} — retrying `
        + `(${config.__gatewayRetryCount}/${MAX_GATEWAY_RETRIES}) after ${delay}ms`
      );
      await sleep(delay);
      return apiClient(config);
    }

    // Handle common error cases
    if (error.response?.status === 401) {
      console.warn('Unauthorized access — signing out');
      supabase.auth.signOut({ scope: 'global' }).finally(() => {
        window.location.href = '/login';
      });
    } else if (error.response?.status === 404) {
      console.warn('Resource not found:', error.response.data);
    } else if (error.response?.status === 422) {
      console.error('Validation Error (422):', error.response.data);
      if (error.response.data?.detail && Array.isArray(error.response.data.detail)) {
        error.response.data.detail.forEach((err, index) => {
          console.error(`   ${index + 1}. ${err.msg || err.message || JSON.stringify(err)}`);
          if (err.loc) console.error(`      Location: ${err.loc.join(' -> ')}`);
        });
      }
    } else if (error.response?.status >= 500) {
      console.error('Server error:', error.response.data);
    }

    return Promise.reject(error);
  }
);

// In-flight coalescing for idempotent GET requests: concurrent callers for the
// exact same URL + query params share one HTTP request instead of opening
// duplicate connections. On initial dashboard mount, ~20 tiles firing at once
// easily saturates the browser's ~6 concurrent HTTP/1.1 connections per origin
// — deduping identical GETs (e.g. 3 different tiles independently requesting
// /object-type-activities for the same file_type) frees slots for everyone
// else, including the metadata/resource-values fetch gating the loading
// screen. Entries are dropped as soon as the request settles — this is NOT a
// response cache, a later call always refetches. Scoped to GET only, and only
// when the caller didn't pass its own AbortController: a POST in this app can
// represent a parameterized query where blind coalescing is less obviously
// safe, and a caller-owned abort signal must control its own request, not one
// shared with unrelated callers.
const inflightGetRequests = new Map();

const stableParamsKey = (params) => {
  if (!params || typeof params !== 'object') return '';
  return JSON.stringify(params, Object.keys(params).sort());
};

/**
 * Generic request wrapper with logging and error handling
 */
export const makeRequest = async (url, config = {}, requestLabel = 'request') => {
  const { method = 'GET', data, ...restConfig } = config;

  const performRequest = async () => {
    const timerId = `${requestLabel} [${Date.now()}]`;
    console.time(timerId);
    try {
      let response;
      if (method.toUpperCase() === 'POST') {
        response = await apiClient.post(url, data, { ...restConfig, responseType: 'json' });
      } else {
        response = await apiClient.get(url, { ...restConfig, responseType: 'json' });
      }

      console.timeEnd(timerId);

      if (response && typeof response.data === 'object' && response.data !== null) {
        console.log(`Successfully fetched ${requestLabel} from ${url}`);
        return response.data;
      } else {
        console.error(`Invalid or missing data in response for ${requestLabel} from ${url}`, response);
        throw new Error(`Received invalid data structure from endpoint for ${requestLabel}.`);
      }
    } catch (error) {
      console.timeEnd(timerId);
      if (isCanceledRequest(error)) {
        throw error;
      }

      console.error(`Error during ${requestLabel} from ${url}:`, error.response || error.message || error);

      if (error.response) {
        console.error(`Error Response Data (${requestLabel}):`, error.response.data);
        console.error(`Error Response Status (${requestLabel}):`, error.response.status);
      } else if (error.request) {
        console.error(`Error Request (${requestLabel}): No response received.`, error.request);
      }

      const errorMsg = error.response?.data?.detail || error.message || 'Unknown API error';
      throw new Error(`Failed to fetch data for ${requestLabel}. Server response: ${errorMsg}`);
    }
  };

  const isCoalescableGet = method.toUpperCase() !== 'POST' && !restConfig.signal;
  if (!isCoalescableGet) {
    return await performRequest();
  }

  const key = `${url}?${stableParamsKey(restConfig.params)}`;
  const inflight = inflightGetRequests.get(key);
  if (inflight) {
    return await inflight;
  }
  const request = performRequest().finally(() => inflightGetRequests.delete(key));
  inflightGetRequests.set(key, request);
  return await request;
};

// In-flight coalescing for fetchHighPriority, mirroring makeRequest's — kept
// separate since this bypasses axios/apiClient entirely (see below).
const inflightHighPriorityRequests = new Map();

/**
 * High-priority GET for the small set of requests that gate the initial
 * "Loading dataset" screen (metadata + resource-values). Bypasses axios's
 * default XHR adapter — XMLHttpRequest has no priority-hint concept — and
 * uses the native fetch() `priority` option (Chrome 101+; a harmless no-op
 * elsewhere) so the browser can schedule these ahead of the ~20+ other tile
 * requests all competing for the same ~6 concurrent HTTP/1.1 connections per
 * origin on initial dashboard mount.
 */
export const fetchHighPriority = async (url, params = {}, requestLabel = 'request') => {
  const key = `${url}?${stableParamsKey(params)}`;
  const inflight = inflightHighPriorityRequests.get(key);
  if (inflight) {
    return await inflight;
  }

  const request = (async () => {
    const qs = new URLSearchParams(params).toString();
    const fullUrl = `${API_BASE_URL}${url}${qs ? `?${qs}` : ''}`;
    const timerId = `${requestLabel} [${Date.now()}]`;
    console.time(timerId);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const headers = { Accept: 'application/json' };
      if (session?.access_token) headers['Authorization'] = `Bearer ${session.access_token}`;

      const response = await fetch(fullUrl, { headers, priority: 'high' });
      console.timeEnd(timerId);

      if (!response.ok) {
        let detail;
        try { detail = (await response.json())?.detail; } catch { /* body not JSON / empty */ }
        if (response.status === 401) {
          console.warn('Unauthorized access — signing out');
          supabase.auth.signOut({ scope: 'global' }).finally(() => { window.location.href = '/login'; });
        }
        throw new Error(detail || `Request failed with status ${response.status}`);
      }
      console.log(`Successfully fetched ${requestLabel} from ${url}`);
      return await response.json();
    } catch (error) {
      console.timeEnd(timerId);
      console.error(`Error during ${requestLabel} from ${url}:`, error);
      throw error instanceof Error ? error : new Error(`Failed to fetch data for ${requestLabel}.`);
    }
  })().finally(() => inflightHighPriorityRequests.delete(key));

  inflightHighPriorityRequests.set(key, request);
  return await request;
};

/**
 * Extended makeRequest for POST requests
 */
export const makePostRequest = async (url, data, config = {}, requestLabel = 'post request') => {
  const timerId = `${requestLabel} [${Date.now()}]`;
  console.time(timerId);
  try {
    const response = await apiClient.post(url, data, { ...config, responseType: 'json' });
    console.timeEnd(timerId);

    if (response && typeof response.data === 'object' && response.data !== null) {
      console.log(`Successfully posted ${requestLabel} to ${url}`);
      return response.data;
    } else {
      console.error(`Invalid or missing data in response for ${requestLabel} from ${url}`, response);
      throw new Error(`Received invalid data structure from endpoint for ${requestLabel}.`);
    }
  } catch (error) {
    console.timeEnd(timerId);
    if (isCanceledRequest(error)) {
      throw error;
    }

    console.error(`Error during ${requestLabel} from ${url}:`, error.response || error.message || error);

    if (error.response) {
      console.error(`Error Response Data (${requestLabel}):`, error.response.data);
      console.error(`Error Response Status (${requestLabel}):`, error.response.status);
    } else if (error.request) {
      console.error(`Error Request (${requestLabel}): No response received.`, error.request);
    }

    const errorMsg = error.response?.data?.detail || error.message || 'Unknown API error';
    throw new Error(`Failed to post data for ${requestLabel}. Server response: ${errorMsg}`);
  }
};
